import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import LoadProgress from '@/components/LoadProgress'
import { Grid } from '@mui/material'

import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-premium'

import {
    defaultMemberAward,
    memberAwardSchema,
    MemberAwardType,
} from '@/api/mem/memberAward'
import {
    memberAwardsQueryOptions,
    useCreateMemberAwardMutation,
    useDeleteMemberAwardMutation,
    useUpdateMemberAwardMutation,
} from '@/api/mem/memberAwardQueryOptions'
import { fetchDetailPermissionsQueryOptions } from '@/api/permissionsQueryOptions'
import CreateDetailAccordion, {
    useCreateDetail,
} from '@/components/detail/CreateDetailRecord'
import EditGrid, { useEditGrid } from '@/components/detail/EditGrid'
import useBasicGridColumns from '@/components/grid/BasicGridColumns'
import useDetailCrudEvents from '@/components/hooks/useDetailCrudEvents'
import useResponsive from '@/components/hooks/useResponsive'
import ScoregDatePicker from '@/components/mui/ScoregDatePicker'
import SelectAward from '@/components/select/SelectAward'
import SelectOrganization from '@/components/select/SelectOrganization'
import SelectSection from '@/components/select/SelectSection'
import { getOrganization } from '@/store/auth/authSlice'
import { yupResolver } from '@hookform/resolvers/yup'
import { TextField } from '@mui/material'
import dayjs from 'dayjs'
import { t } from 'i18next'
import { useSelector } from 'react-redux'

export const Route = createFileRoute(
    '/_auth/member_/$memberUuid/awards',
)({
    component: MemberComponent,
})

function RenderAwardOrganization(props: GridRenderCellParams<any, object>) {
    return (<>
        <Grid container spacing={0}>
            <Grid size={12}>
                <span>{props.row.award.name}</span>
            </Grid>
            <Grid size={12}>
                <span>{props.row.organization?.name}</span>
            </Grid>
        </Grid>
    </>);
}

function MemberComponent() {
    const params = Route.useParams()
    const memberAwardsQuery = useSuspenseQuery(
        memberAwardsQueryOptions(params.memberUuid),
    )
    const memberAwards = memberAwardsQuery.data

    const permissionsQuery = useSuspenseQuery(
        fetchDetailPermissionsQueryOptions('member', params.memberUuid, [
            'MemberAward',
        ]),
    )
    const permissions = permissionsQuery.data

    const createable =
        permissions &&
        permissions.filter(
            (p) =>
                p.detail === 'MemberAward' && p.permissions.some((a) => a === 'create'),
        ).length > 0

    const { onCreated, onUpdated, onDeleted, onError } =
        useDetailCrudEvents<MemberAwardType>('memberAward')

    const createMemberAwardMutation = useCreateMemberAwardMutation(
        params.memberUuid,
        onCreated,
        onError,
    )
    const updateMemberAwardMutation = useUpdateMemberAwardMutation(
        params.memberUuid,
        onUpdated,
        onError,
    )
    const deleteMemberAwardMutation = useDeleteMemberAwardMutation(
        params.memberUuid,
        onDeleted,
        onError,
    )

    const basicGridColumns = useBasicGridColumns()
    const sessionOrganization = useSelector(getOrganization)

    const onSubmit = (data: MemberAwardType) => {
        createMemberAwardMutation.mutate(data)
    }

    const { xsmall, small, medium, large, xlarge } = useResponsive()

    const columns: GridColDef[] = []
    if (xsmall || small || medium) {
        columns.push({
            field: 'award_organization',
            valueGetter: (_value, row) => `${row}`,
            headerName: 'Verleihung',
            minWidth: 150,
            renderCell: RenderAwardOrganization,
        })
    }
    if (large || xlarge) {
        columns.push(
            basicGridColumns.awardNameColumn,
            basicGridColumns.organizationNameColumn,
            basicGridColumns.sectionAbbreviationColumn,
        )
    }
    columns.push({
        field: 'awardingDate',
        headerName: t('property.memberAward.awardingDate'),
        type: 'date',
        valueGetter: (value) => value && new Date(value),
        align: 'right',
        minWidth: 100,
        maxWidth: 170,
        headerAlign: 'right',
        editable: true,
    })
    if (large || xlarge) {
        columns.push(basicGridColumns.descriptionColumn)
    }

    const createDetail = useCreateDetail<MemberAwardType>({
        onSubmit,
        resolver: yupResolver<MemberAwardType, any, any>(memberAwardSchema),
        defaultValues: {
            ...defaultMemberAward,
            member: {
                uuid: params.memberUuid,
            },
            organization: {
                uuid: sessionOrganization.uuid,
            },
        },
        title: t('action.assign', { entity: t('entity.award') }),
    })

    const editGrid = useEditGrid<MemberAwardType>({
        defaultValues: defaultMemberAward,
        updateMutation: updateMemberAwardMutation,
        deleteMutation: deleteMemberAwardMutation,
        records: memberAwards,
        columns: columns,
        title: t('entity.award'),
    })

    if (memberAwardsQuery.isLoading || memberAwardsQuery.isFetching) {
        return <LoadProgress />
    }
    if (permissionsQuery.isLoading || permissionsQuery.isFetching) {
        return <LoadProgress />
    }

    return (<>
        {/*JSON.stringify({
        xs: xsmall,
        sm: small,
        md: medium, 
        lg: large,
        xl: xlarge,
      })*/}
        {createable && (
            <CreateDetailAccordion {...createDetail}>
                <Grid container spacing={2}>
                    <input
                        type="hidden"
                        defaultValue={params.memberUuid}
                        {...createDetail.form.register('member.uuid')}
                    />
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <SelectAward control={createDetail.form.control} name="award.uuid" />
                    </Grid>
                    {(sessionOrganization.organizationLevel === 'DIVISION' ||
                        sessionOrganization.organizationLevel === 'FEDERATION') && (
                            <Grid
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 4
                                }}>
                                <SelectOrganization control={createDetail.form.control} name="organization.uuid" />
                            </Grid>
                        )}
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <SelectSection
                            control={createDetail.form.control}
                            name="section.uuid"
                            optional={true}
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 6,
                            lg: 4
                        }}>
                        <ScoregDatePicker
                            name="awardingDate"
                            defaultValue={dayjs(defaultMemberAward.awardingDate)}
                            disabled={false}
                            control={createDetail.form.control}
                            label={t('property.memberAward.awardingDate')}
                        />
                    </Grid>
                    <Grid
                        size={{
                            xs: 12,
                            md: 12,
                            lg: 12
                        }}>
                        <TextField
                            label={t('property.memberAward.description')}
                            fullWidth
                            {...createDetail.form.register('description')}
                        />
                    </Grid>
                    <></>
                </Grid>
            </CreateDetailAccordion>
        )}
        <EditGrid {...editGrid} detailLink="/member/$memberUuid/memberaward/$uuid" parentUuid={(entity) => entity.member.uuid} />
    </>);
}
